import { LocaleLink } from "@/components/locale-link";
import { ArrowUpRight } from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { siteMobileGitHubUrl, siteXUrl } from "@/config/site";
import { useTranslation } from "@/common/providers/i18n-provider";
import type { ImageSizes } from "@/lib/image-sizes";
import "@/styles/company-pages.css";

/** The only images this page renders; verified against the generated catalog. */
const IMAGE_SIZES = {
  "/img/about-us/craft.webp": { width: 1376, height: 768 },
  "/img/about-us/community.webp": { width: 1376, height: 768 },
} satisfies ImageSizes;

export function AboutPage() {
  const { t } = useTranslation();
  return (
    <div className="company-page about-page">
      <AppHeader />
      <main id="main">
        <section className="company-container about-hero">
          <div className="about-hero-copy">
            <h1>{t("about.hero.h1")}</h1>
            <p className="company-lead">{t("about.hero.mission")}</p>
            <div className="company-actions">
              <LocaleLink to="/beancount-cloud" className="company-button">
                {t("about.hero.cta.cloud")}
              </LocaleLink>
              <LocaleLink to="/docs" className="company-text-link">
                {t("about.hero.cta.docs")}
              </LocaleLink>
            </div>
          </div>
          <img
            src="/img/about-us/craft.webp"
            alt={t("about.photo.craft.alt")}
            width={IMAGE_SIZES["/img/about-us/craft.webp"].width}
            height={IMAGE_SIZES["/img/about-us/craft.webp"].height}
            fetchPriority="high"
            decoding="async"
            className="about-hero-photo"
          />
        </section>

        <section
          className="about-story company-container"
          aria-labelledby="about-build-title"
        >
          <div>
            <h2 id="about-build-title">{t("about.what-we-build.label")}</h2>
            <LocaleLink to="/blog" className="company-text-link">
              {t("about.hero.cta.blog")}
            </LocaleLink>
          </div>
          <div className="about-story-copy">
            <p>{t("about.what-we-build.p1")}</p>
            <p className="about-principle">{t("about.what-we-build.quote")}</p>
            <p>{t("about.what-we-build.p2")}</p>
          </div>
        </section>

        <section
          className="about-foundation"
          aria-labelledby="about-source-title"
        >
          <div className="company-container">
            <h2 id="about-source-title">{t("about.stack.eyebrow")}</h2>
            <div className="about-projects">
              <a
                href="https://beancount.github.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="about-project"
              >
                <div className="about-project-heading">
                  <h3>{t("about.stack.beancount.name")}</h3>
                  <ArrowUpRight size={24} aria-hidden="true" />
                </div>
                <p>{t("about.stack.beancount.desc")}</p>
                <span className="about-project-url" dir="ltr">
                  {t("about.stack.beancount.url")}
                </span>
              </a>
              <a
                href="https://beancount.github.io/fava/"
                target="_blank"
                rel="noopener noreferrer"
                className="about-project"
              >
                <div className="about-project-heading">
                  <h3>{t("about.stack.fava.name")}</h3>
                  <ArrowUpRight size={24} aria-hidden="true" />
                </div>
                <p>{t("about.stack.fava.desc")}</p>
                <span className="about-project-url" dir="ltr">
                  {t("about.stack.fava.url")}
                </span>
              </a>
            </div>
          </div>
        </section>

        <section
          className="company-container about-community"
          aria-labelledby="about-community-title"
        >
          <img
            src="/img/about-us/community.webp"
            alt={t("about.photo.community.alt")}
            width={IMAGE_SIZES["/img/about-us/community.webp"].width}
            height={IMAGE_SIZES["/img/about-us/community.webp"].height}
            loading="lazy"
            decoding="async"
          />
          <div>
            <h2 id="about-community-title">{t("about.contact.label")}</h2>
            <p>{t("about.contact.intro")}</p>
            <ul className="company-link-list">
              <li>
                <a
                  href="https://t.me/beancount"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("about.contact.telegram")}
                  <ArrowUpRight size={18} aria-hidden="true" />
                </a>
              </li>
              <li>
                <a
                  href={siteXUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("about.contact.twitter")}
                  <ArrowUpRight size={18} aria-hidden="true" />
                </a>
              </li>
              <li>
                <a
                  href={siteMobileGitHubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("about.contact.github")}
                  <ArrowUpRight size={18} aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
